import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ActionsFlagHighlightedXsmall from '@strava/icons/ActionsFlagHighlightedXsmall';
import Button from '@strava/ui/Button';
import colors from '@strava/ui/colors';
import { Markup } from 'interweave';
import { useFeedEntityContext } from '../../FeedEntityContext';

import styles from '../index.scss';

const ELLIPSIS = '•••';
const MAX_LENGTH = 350;

const ActivityTextBody = ({
  activityId,
  activityName,
  ownedByCurrentAthlete,
  flagged,
  description
}) => {
  const { feedEntityState } = useFeedEntityContext();
  const [readMore, setReadMore] = useState(false);

  const isExpandable = description && description.length > MAX_LENGTH;

  const splitDescription = (() => {
    if (description) {
      const cleanDescription = description.trim();

      if (cleanDescription.length < MAX_LENGTH) {
        return [cleanDescription, ''];
      }
      // trim the string to the maximum length
      let firstSection = cleanDescription.substr(0, MAX_LENGTH);
      // re-trim if we are in the middle of a word
      firstSection = firstSection.substr(
        0,
        Math.min(firstSection.length, firstSection.lastIndexOf(' '))
      );
      // retrim if in the middle of a link
      if (
        firstSection.lastIndexOf('<a') >= 0 &&
        (!(firstSection.lastIndexOf('</a>') > 0) ||
          firstSection.lastIndexOf('</a>') < firstSection.lastIndexOf('<a'))
      ) {
        firstSection = firstSection
          .substr(0, firstSection.lastIndexOf('<a'))
          .trim();
      }
      return [firstSection, cleanDescription.substr(firstSection.length)];
    }
    return ['', ''];
  })();

  const handleMentionClick = (e) => {
    const isEntityClub = e.target.href.includes('club');
    const entityId = e.target.href.match(/\/([^/]+)$/)[1];
    feedEntityState.trackAnalytics({
      action: 'click',
      element: 'mention',
      properties: {
        clicked_athlete_id: isEntityClub ? null : entityId,
        clicked_club_id: isEntityClub ? entityId : null
      }
    });
  };

  /** This is a transform function for an Interweave Markup element.
   * It adds click tracking to mentions. https://interweave.dev/docs/
   * @returns {JSX.Element|undefined}
   */
  const addMentionClickTracking = (elem) => {
    if (elem.nodeName === 'A' && elem.className === 'mention') {
      return (
        <a
          href={elem.href}
          className={elem.className}
          onClick={handleMentionClick}
        >
          {elem.innerText}
        </a>
      );
    }

    // Don't modify the element.
    return undefined;
  };

  const content = readMore ? splitDescription.join('') : splitDescription[0];

  return (
    <>
      {/* Title Text */}
      <h3 className={`text-title-1 ${styles.activityName}`}>
        <a
          href={`/activities/${activityId}`}
          className={styles.activityName}
          data-testid="activity_name"
          onClick={() => {
            feedEntityState.trackAnalytics({
              action: 'click',
              properties: { element_source: 'title' }
            });
          }}
        >
          {activityName}
        </a>
        {ownedByCurrentAthlete && flagged && (
          <span className={styles.iconWrapper}>
            <ActionsFlagHighlightedXsmall
              fill={colors.BRAND_DANGER}
              data-testid="activity_flag"
            />
          </span>
        )}
      </h3>

      {/* Activity Description */}
      {description && (
        <div className={styles.descriptionBody}>
          <div
            className={styles.wrapper}
            data-testid="activity_description_wrapper"
          >
            <Markup
              noWrap={true}
              content={content}
              disableLineBreaks={true}
              transform={addMentionClickTracking}
            />
          </div>
          {isExpandable && (
            <div className={styles.descriptionReadMore}>
              <Button
                data-testid="activity_description_read_more"
                tabIndex={0}
                type="button"
                onClick={() => {
                  setReadMore(!readMore);
                }}
              >
                {ELLIPSIS}
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

ActivityTextBody.propTypes = {
  activityId: PropTypes.string.isRequired,
  activityName: PropTypes.string.isRequired,
  ownedByCurrentAthlete: PropTypes.bool,
  flagged: PropTypes.bool,
  description: PropTypes.string
};

ActivityTextBody.defaultProps = {
  ownedByCurrentAthlete: false,
  flagged: false,
  description: PropTypes.string
};

export default ActivityTextBody;
